import {
  ExtendableImage,
  ExtendableImageProps,
} from '@design-system/extendable-image';
import {
  RetailerLink,
  RetailersBlockProps,
} from '@design-system/retailers-block';
import {
  getLocalStorageData,
  setLocalStorageData,
} from '@phx-husky/local-storage';
import React from 'react';

import { BodyRenderExternalData } from '~/components/BodyRender/models';
import { RetailersPageBlockProps } from '~/components/RetailersPageBlock/models';
import TMPLinkWrap from '~/components/TMPLinkWrap';
import { imageMapper } from '~/mappers/image';
import { linkMapper } from '~/mappers/link';
import { pageBlockMapper } from '~/mappers/pageBlock';
import { gtmService } from '~/services/gtm';
import { SalsifyProductData } from '~/types/common';
import { GTM_LOCALSTORAGE_DATA } from '~/utils/constants';

import { ProductCardProductData } from '../card/models';
import { Retailers, RetailersBase, RetailersPageBlock } from './models';

const retailersBaseMapper = ({ type, items }: RetailersBase) => ({
  type,
  retailerLinks: items.map((item) => {
    const image = imageMapper(item.image) as ExtendableImageProps;
    const linkItem = { ...item.link, variant: 'retailer', size: 'large' };

    return {
      link: { component: TMPLinkWrap, props: linkMapper(linkItem) },
      titleAsText: item.titleAsText,
      children: <ExtendableImage {...image} />,
    } as RetailerLink;
  }),
});

export const retailersMapper = (
  retailers: Retailers,
  productData: SalsifyProductData | ProductCardProductData
): RetailersBlockProps => {
  const onClickRetailerName = (title: string, url: string) => {
    setLocalStorageData(GTM_LOCALSTORAGE_DATA.lastRetailerTitle, title);
    const lastProductIndex = getLocalStorageData(
      GTM_LOCALSTORAGE_DATA.lastProductIndex
    );
    gtmService.emitRetailerClick(title, url);
    gtmService.emitSelectPromotion(productData, title, lastProductIndex);
  };

  return {
    ...retailersBaseMapper(retailers),
    label: retailers.label,
    titleOptions: retailers.labelSettings,
    onClickRetailerName,
    isDropdown: retailers.isDropdown,
    buttonLabel: retailers.buttonLabel,
  };
};

export const retailersBlockMapper = (
  retailers: RetailersPageBlock,
  _currentPageUid: string,
  externalData: BodyRenderExternalData
): RetailersPageBlockProps => {
  const importRetailers = retailers.importRetailersFromPDP;
  const importedRetailers = externalData?.retailers;
  const items = importRetailers
    ? importedRetailers?.[0]?.items ?? retailers.items
    : retailers.items;

  const retailersToRender = {
    ...retailers,
    items: items || [],
  };

  return {
    pageBlock: pageBlockMapper(retailers.pageBlock),
    componentId: retailers.componentId,
    retailers: retailersBaseMapper(retailersToRender),
    uid: retailers.uid,
    isDropdown: retailers.isDropdown,
    buttonLabel: retailers.buttonLabel,
  };
};
